import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Trophy, Flame, Sparkles, ArrowRight, Phone, Monitor, Mail, CheckSquare } from 'lucide-react';
import crm, { STAGES, TASK_TYPES } from '../crm/api';

const SRC = {
  microsite: { label: 'Microsite', color: '#0e7490' },
  whatsapp: { label: 'WhatsApp', color: '#22c55e' },
  card: { label: 'Tap', color: '#c99700' },
  form: { label: 'Form', color: '#8b5cf6' },
  other: { label: 'Other', color: '#94a3b8' },
};
const stageColor = (k) => (STAGES.find((s) => s.key === k) || {}).color || '#94a3b8';
// Follow-ups are typed — surface which kind rather than a bare title.
const TICON = { call: Phone, meeting: Users, demo: Monitor, email: Mail, todo: CheckSquare };
const tLabel = (t) => (TASK_TYPES.find((x) => x.key === t) || {}).label || 'To-do';
const AV = [['#10b981', '#0e7490'], ['#6366f1', '#8b5cf6'], ['#f59e0b', '#c99700'], ['#f43f5e', '#fb7185'], ['#0ea5e9', '#0e7490'], ['#14b8a6', '#0b6e3e']];
const grad = (n) => { const g = AV[(n || 'x').charCodeAt(0) % AV.length]; return `linear-gradient(135deg,${g[0]},${g[1]})`; };

/* ---------- tiny charts ---------- */
function Ring({ pct, size = 132, stroke = 13, color = '#10b981' }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r, off = c * (1 - pct / 100);
  return (
    <svg width={size} height={size} className="block">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f5" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset .6s ease' }} />
    </svg>
  );
}
function DonutMulti({ segments, size = 150, stroke = 20 }) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const r = (size - stroke) / 2, c = 2 * Math.PI * r; let acc = 0;
  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f5" strokeWidth={stroke} />
      {segments.map((s, i) => {
        const frac = s.value / total, dash = `${c * frac} ${c}`, off = -c * acc; acc += frac;
        return <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={stroke}
          strokeDasharray={dash} strokeDashoffset={off} transform={`rotate(-90 ${size / 2} ${size / 2})`} />;
      })}
    </svg>
  );
}

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function Dashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  useEffect(() => {
    crm.get('/overview').then(({ data }) => setD(data)).catch(() => setD({}));
    crm.get('/tasks', { params: { filter: 'today' } }).then(({ data }) => setTodayTasks(data.tasks || [])).catch(() => {});
  }, []);
  if (!d) return <div className="pt-10 text-[var(--muted)]">Loading…</div>;

  const days = (d.days || []).map((x) => x.count);
  const week = days.reduce((a, b) => a + b, 0);
  // Solid gradient fills, same treatment as the Visitors page — but a lighter,
  // brighter stop pair so they read as their own set rather than a copy.
  const kpis = [
    { label: 'Total Leads', value: d.total || 0, icon: Users, from: '#2dd4bf', to: '#0891b2', cap: `+${week} this week` },
    { label: 'New', value: d.newThisWeek || 0, icon: Sparkles, from: '#60a5fa', to: '#2563eb', cap: 'last 7 days' },
    { label: 'Hot', value: d.hot || 0, icon: Flame, from: '#fb923c', to: '#ef4444', cap: 'high priority' },
    { label: 'Won', value: d.won || 0, icon: Trophy, from: '#4ade80', to: '#0b8a4f', cap: 'closed deals' },
  ];
  const maxStage = Math.max(1, ...STAGES.map((s) => d.byStatus?.[s.key] || 0));
  const totalLeads = d.total || 0;
  const srcSeg = Object.entries(d.bySource || {}).map(([k, v]) => ({ value: v, color: (SRC[k] || SRC.other).color, label: (SRC[k] || SRC.other).label }));

  return (
    <div className="space-y-5 max-w-[1180px] pt-1">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="rounded-3xl p-5 text-white card-lux"
              style={{ background: `linear-gradient(135deg, ${k.from}, ${k.to})` }}>
              <div className="flex items-center justify-center gap-2.5">
                <Icon size={22} className="opacity-95 shrink-0" />
                <div className="text-[19px] font-extrabold tracking-tight leading-tight truncate">{k.label}</div>
              </div>
              <div className="text-center mt-3">
                <div className="text-[32px] font-extrabold tracking-tight leading-none">{k.value}</div>
                <div className="text-[13px] font-bold mt-1.5" style={{ color: 'rgba(255,255,255,.88)' }}>{k.cap}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* forecast + follow-ups */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[var(--card)] rounded-3xl border border-[var(--border)] p-6 card-lux">
          <div className="flex items-center justify-between mb-4"><h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight">Deal forecast</h3><span className="text-[12px] text-[var(--muted)]">open pipeline vs won</span></div>
          <div className="flex items-end gap-10">
            <div><div className="text-[13px] font-semibold text-[var(--muted)]">Pipeline value</div><div className="text-[30px] font-extrabold text-[var(--text)] leading-tight">{inr(d.pipelineValue)}</div></div>
            <div><div className="text-[13px] font-semibold text-[var(--muted)]">Won value</div><div className="text-[30px] font-extrabold leading-tight" style={{ color: '#0b6e3e' }}>{inr(d.wonValue)}</div></div>
          </div>
          <div className="mt-4 h-3 rounded-full overflow-hidden" style={{ background: 'var(--soft)' }}>
            <div className="h-full rounded-full" style={{ width: `${(d.pipelineValue + d.wonValue) ? Math.round(d.wonValue / (d.pipelineValue + d.wonValue) * 100) : 0}%`, background: 'linear-gradient(90deg,#10b981,#0b6e3e)' }} />
          </div>
        </div>

        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] p-6 card-lux">
          <div className="flex items-center justify-between mb-3"><h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight">Follow-ups</h3><button onClick={() => nav('/tasks')} className="text-[12px] font-bold text-[var(--acc2)] hover:underline">All</button></div>
          <div className="flex gap-3 mb-3">
            <div className="flex-1 rounded-2xl p-3 text-center" style={{ background: 'var(--acc-soft)' }}><div className="text-2xl font-extrabold" style={{ color: 'var(--acc2)' }}>{d.tasksDue || 0}</div><div className="text-[11px] text-[var(--muted)]">Due today</div></div>
            <div className="flex-1 rounded-2xl p-3 text-center" style={{ background: 'var(--soft)' }}><div className="text-2xl font-extrabold text-[var(--text)]">{d.tasksOpen || 0}</div><div className="text-[11px] text-[var(--muted)]">Open</div></div>
          </div>
          <div className="space-y-2 max-h-32 overflow-auto">
            {todayTasks.length === 0 && <div className="text-[12px] text-[var(--muted)]">Nothing due today 🎉</div>}
            {todayTasks.slice(0, 4).map((t) => {
              const TI = TICON[t.type] || CheckSquare;
              return (
                <div key={t.id} className="flex items-start gap-2 text-[13px]">
                  <span className="w-5 h-5 rounded-md shrink-0 flex items-center justify-center mt-0.5" style={{ background: 'var(--acc-soft)' }}>
                    <TI size={11} style={{ color: 'var(--acc2)' }} />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate font-semibold" style={{ color: 'var(--text)' }}>{t.title}</div>
                    <div className="text-[11px] truncate" style={{ color: 'var(--muted)' }}>{tLabel(t.type)}{t.Lead ? ` · ${t.Lead.name}` : ''}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* team performance */}
      <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] p-5 card-lux">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight">Team performance</h3>
          <button onClick={() => nav('/employees')} className="text-[12px] font-bold text-[var(--acc2)] hover:underline">View team</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { n: d.team?.total || 0, l: 'Team members', c: 'var(--acc2)', bg: 'var(--acc-soft)' },
            { n: d.team?.performing || 0, l: 'Performing', c: '#0b8a4f', bg: 'rgba(11,138,79,.12)' },
            { n: d.team?.needsImprovement || 0, l: 'Needs improvement', c: '#c2681a', bg: 'rgba(217,119,6,.14)' },
            { n: d.team?.idle || 0, l: 'No leads yet', c: 'var(--muted)', bg: 'var(--soft)' },
          ].map((t) => (
            <div key={t.l} className="rounded-2xl p-4 text-center" style={{ background: t.bg }}>
              <div className="text-[30px] font-extrabold leading-none" style={{ color: t.c }}>{t.n}</div>
              <div className="text-[12px] font-semibold text-[var(--muted)] mt-1.5">{t.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* insights: sources + top reps + win rate (before pipeline) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Sources */}
        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] p-6 card-lux">
          <h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight mb-4">Sources</h3>
          {srcSeg.length === 0 ? <div className="text-sm text-[var(--muted)]">No data.</div> : (
            <div className="flex items-center gap-5">
              <DonutMulti segments={srcSeg} size={118} stroke={17} />
              <div className="space-y-2 flex-1">
                {srcSeg.map((s) => (
                  <div key={s.label} className="flex items-center gap-2 text-[13px]"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /><span className="font-semibold text-[var(--muted)] flex-1">{s.label}</span><span className="text-[var(--muted)] font-bold">{s.value}</span></div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Top Reps */}
        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] p-6 card-lux">
          <h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight mb-4">Top Reps</h3>
          {(d.leaderboard || []).length === 0 ? <div className="text-sm text-[var(--muted)]">No data.</div> : (
            <div className="space-y-3">
              {(d.leaderboard || []).map((r, i) => (
                <div key={r.name} className="flex items-center gap-3">
                  <span className="text-base w-6 text-center">{['🥇', '🥈', '🥉'][i] || <span className="text-slate-300 font-bold text-sm">{i + 1}</span>}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between"><span className="text-[13px] font-bold text-[var(--text)] truncate">{r.name}</span><span className="text-[12px] text-[var(--muted)] font-semibold">{r.won}w / {r.total}</span></div>
                    <div className="h-1.5 bg-[var(--soft)] rounded-full mt-1.5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(r.total / (d.leaderboard[0].total || 1)) * 100}%`, background: 'linear-gradient(90deg,#10b981,#0e7490)' }} /></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Win rate */}
        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] p-6 card-lux flex flex-col items-center justify-center">
          <h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight self-start">Win rate</h3>
          <div className="relative my-3">
            <Ring pct={d.conversion || 0} color="#10b981" />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-[32px] font-extrabold text-[var(--text)] leading-none">{d.conversion || 0}%</div>
              <div className="text-[11px] text-[var(--muted)] font-semibold mt-1">{d.won || 0} of {totalLeads} won</div>
            </div>
          </div>
          <div className="flex gap-4 text-[12px] font-semibold">
            <span className="text-[var(--muted)]"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />Won {d.won || 0}</span>
            <span className="text-[var(--muted)]"><span className="inline-block w-2 h-2 rounded-full bg-slate-300 mr-1" />Open {d.open || 0}</span>
          </div>
        </div>
      </div>

      {/* pipeline + recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Pipeline (wide) */}
        <div className="lg:col-span-2 bg-[var(--card)] rounded-3xl border border-[var(--border)] p-6 card-lux">
          <div className="flex items-center justify-between mb-5">
            <div><h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight">Pipeline</h3><p className="text-[12px] text-[var(--muted)]">{totalLeads} leads across stages</p></div>
            <button onClick={() => nav('/leads')} className="text-[13px] font-bold text-[var(--acc2)] hover:gap-2 flex items-center gap-1 transition-all">Open board <ArrowRight size={14} /></button>
          </div>
          <div className="space-y-3.5">
            {STAGES.map((s) => {
              const c = d.byStatus?.[s.key] || 0, pct = totalLeads ? Math.round((c / totalLeads) * 100) : 0;
              return (
                <div key={s.key} className="flex items-center gap-4">
                  <div className="w-24 flex items-center gap-2 shrink-0"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /><span className="text-[13px] font-semibold text-[var(--muted)]">{s.label}</span></div>
                  <div className="flex-1 h-9 bg-[var(--soft)] rounded-xl overflow-hidden">
                    <div className="h-full rounded-xl flex items-center px-3 text-white text-[12px] font-bold" style={{ width: `${Math.max(9, (c / maxStage) * 100)}%`, background: `linear-gradient(90deg,${s.color}dd,${s.color})`, transition: 'width .5s' }}>{c > 0 && c}</div>
                  </div>
                  <span className="w-10 text-right text-[13px] font-bold text-[var(--muted)]">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
            <h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight">Recent Leads</h3>
            <button onClick={() => nav('/leads')} className="text-[13px] font-bold text-[var(--acc2)] hover:underline">See all</button>
          </div>
          <div className="divide-y divide-[var(--border)]">
            {(d.recent || []).length === 0 && <div className="px-6 py-10 text-center text-[var(--muted)] text-sm">No leads yet.</div>}
            {(d.recent || []).slice(0, 6).map((l) => (
              <div key={l.id} className="flex items-center gap-3.5 px-6 py-3.5 hover:bg-[var(--soft)] transition-colors">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm" style={{ background: grad(l.name) }}>{(l.name || '?').slice(0, 1).toUpperCase()}</div>
                <div className="min-w-0 flex-1"><div className="font-bold text-[var(--text)] text-[14px] truncate">{l.name || '(no name)'}</div><div className="text-[12px] text-[var(--muted)] truncate">{l.company || l.email || l.phone || '—'} · {l.owner?.name}</div></div>
                <span className="text-[11px] font-bold px-2.5 py-1 rounded-lg shrink-0" style={{ color: stageColor(l.status), background: `${stageColor(l.status)}18` }}>{l.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
